import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getTool, listCategories, listLocations, listMaintenanceSchedules, logMaintenanceService } from "@/lib/firestore";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import {
  Wrench,
  ArrowLeft,
  MapPin,
  Package,
  Printer,
  ArrowsClockwise,
  Warning,
  Timer,
  CheckCircle,
  User,
  Clock,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import jsPDF from "jspdf";

export default function ToolDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const nav = useNavigate();
  const [tool, setTool] = useState(null);
  const [category, setCategory] = useState(null);
  const [location, setLocation] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [serviceOn, setServiceOn] = useState(null);
  const [serviceNotes, setServiceNotes] = useState("");
  const qrRef = useRef(null);

  const loadSchedules = async (toolId) => {
    try {
      const data = await listMaintenanceSchedules({ tool_id: toolId });
      setSchedules(data);
    } catch {}
  };

  useEffect(() => {
    (async () => {
      try {
        const data = await getTool(id);
        setTool(data);
        if (data.category_id) {
          const c = await listCategories();
          setCategory(c.find((x) => x.id === data.category_id));
        }
        if (data.location_id) {
          const l = await listLocations();
          setLocation(l.find((x) => x.id === data.location_id));
        }
        await loadSchedules(data.id);
      } catch (e) {
        toast.error(e.message || "Tool not found");
        nav("/tools");
      }
    })();
  }, [id, nav]);

  const logService = async (e) => {
    e.preventDefault();
    try {
      await logMaintenanceService(serviceOn.id, serviceNotes, user);
      toast.success("Service logged");
      setServiceOn(null);
      setServiceNotes("");
      await loadSchedules(tool.id);
    } catch (e) { toast.error(e.message); }
  };

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `${tool.tool_code}_qr.png`;
    a.click();
  };

  const downloadPDFLabel = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas || !tool) return;
    const doc = new jsPDF({ unit: "mm", format: [80, 100] });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("MTM", 40, 8, { align: "center" });
    doc.setFontSize(7);
    doc.setTextColor(120);
    doc.text("MACHINE SHOP OPS", 40, 12, { align: "center" });
    doc.addImage(canvas.toDataURL("image/png"), "PNG", 15, 16, 50, 50);
    doc.setTextColor(0);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text(tool.name, 40, 74, { align: "center", maxWidth: 70 });
    doc.setFontSize(8);
    doc.setFont("courier", "normal");
    doc.text(`#${tool.tool_code}`, 40, 80, { align: "center" });
    if (location) doc.text(`${location.name}${tool.bin ? " · " + tool.bin : ""}`, 40, 86, { align: "center" });
    doc.setFontSize(6);
    doc.setTextColor(120);
    doc.text("Scan to view tool", 40, 94, { align: "center" });
    doc.save(`${tool.tool_code}_label.pdf`);
  };

  if (!tool) {
    return <div className="font-mono text-[#52525B] blink">// loading tool //</div>;
  }

  const history = tool.history || [];

  return (
    <div className="space-y-6">
      <Link to="/tools" className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white transition-colors duration-150">
        <ArrowLeft size={16} /> Back to Tools
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main info */}
        <div className="brutal-card p-6 lg:col-span-2">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Wrench size={14} weight="bold" className="text-[#FF4F00]" />
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF4F00]">
                  {category?.name || "TOOL"}
                </div>
              </div>
              <h1 className="font-display font-black text-3xl md:text-4xl leading-tight">{tool.name}</h1>
              <div className="font-mono text-sm text-[#A1A1AA] mt-1">#{tool.tool_code}</div>
            </div>
            <div
              className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${
                tool.condition === "good"
                  ? "border-[#00E676] text-[#00E676]"
                  : tool.condition === "fair"
                  ? "border-[#FFD700] text-[#FFD700]"
                  : "border-[#D32F2F] text-[#D32F2F]"
              }`}
            >
              {tool.condition}
            </div>
          </div>

          {tool.description && <p className="text-[#A1A1AA] mb-4">{tool.description}</p>}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 border-t border-[#1a1a1e] pt-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#52525B]">Total</div>
              <div className="font-mono text-2xl font-bold">{tool.quantity}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#52525B]">Available</div>
              <div className="font-mono text-2xl font-bold text-[#FF4F00]">{tool.available_quantity}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#52525B]">Location</div>
              <div className="text-sm flex items-center gap-1">
                <MapPin size={12} className="text-[#FF4F00]" /> {location?.name || "—"}
              </div>
              {tool.bin && <div className="font-mono text-xs text-[#A1A1AA]">Bin: {tool.bin}</div>}
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#52525B]">Added</div>
              <div className="font-mono text-xs">{new Date(tool.created_at).toLocaleDateString()}</div>
            </div>
          </div>
        </div>

        {/* QR Code */}
        <div className="brutal-card p-6" data-testid="tool-qr-panel">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA] mb-3">// QR CODE</div>
          <div className="bg-white p-4 flex items-center justify-center" ref={qrRef}>
            <QRCodeCanvas
              value={`MTM:${tool.tool_code}`}
              size={180}
              level="M"
              includeMargin={false}
              data-testid="tool-qr-code"
            />
          </div>
          <div className="font-mono text-xs text-center mt-2 text-[#A1A1AA]">MTM:{tool.tool_code}</div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={downloadQR}
              data-testid="download-qr-btn"
              className="brutal-btn flex-1 border border-[#27272A] hover:border-[#FF4F00] hover:text-[#FF4F00] py-2 text-xs transition-colors duration-150"
            >
              PNG
            </button>
            <button
              onClick={downloadPDFLabel}
              data-testid="download-qr-pdf-btn"
              className="brutal-btn flex-1 bg-[#FF4F00] text-black py-2 text-xs flex items-center justify-center gap-1 hover:bg-[#FF6B2B] transition-colors duration-150"
            >
              <Printer size={12} weight="bold" /> Label PDF
            </button>
          </div>
        </div>
      </div>

      {/* Maintenance Section */}
      <div className="brutal-card p-6" data-testid="tool-maintenance-panel">
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA]">// PREVENTIVE MAINTENANCE</div>
            <h2 className="font-display font-bold text-xl">Schedules</h2>
          </div>
          <Link
            to="/maintenance"
            className="text-xs text-[#FF4F00] hover:underline font-mono uppercase tracking-widest"
          >
            Manage all →
          </Link>
        </div>
        {schedules.length === 0 ? (
          <div className="text-center py-8 text-[#52525B] font-mono">
            // no maintenance schedules for this tool //
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {schedules.map((s) => (
              <div
                key={s.id}
                data-testid={`tool-schedule-${s.id}`}
                className={`border border-[#27272A] p-4 ${
                  s.is_overdue ? "border-l-4 border-l-[#D32F2F]" : s.is_due_soon ? "border-l-4 border-l-[#FFD700]" : ""
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-semibold text-sm flex items-center gap-2">
                      {s.is_overdue ? (
                        <Warning size={14} weight="bold" className="text-[#D32F2F]" />
                      ) : s.is_due_soon ? (
                        <Clock size={14} weight="bold" className="text-[#FFD700]" />
                      ) : (
                        <Timer size={14} weight="bold" className="text-[#00E676]" />
                      )}
                      {s.name}
                    </div>
                    <div className="font-mono text-[10px] text-[#52525B] mt-1">
                      every{" "}
                      {s.interval_days ? `${s.interval_days}d` : ""}
                      {s.interval_days && s.interval_uses ? " / " : ""}
                      {s.interval_uses ? `${s.interval_uses} uses` : ""}
                    </div>
                  </div>
                  <span
                    className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${
                      s.is_overdue ? "border-[#D32F2F] text-[#D32F2F]" :
                      s.is_due_soon ? "border-[#FFD700] text-[#FFD700]" :
                      "border-[#00E676] text-[#00E676]"
                    }`}
                  >
                    {s.is_overdue ? "OVERDUE" : s.is_due_soon ? "DUE" : "OK"}
                  </span>
                </div>
                <div className="text-xs text-[#A1A1AA] mb-2 font-mono">
                  Last: {s.last_serviced_at ? new Date(s.last_serviced_at).toLocaleDateString() : "—"} · Uses: {s.uses_since_service || 0}
                </div>
                <button
                  onClick={() => { setServiceNotes(""); setServiceOn(s); }}
                  data-testid={`tool-service-${s.id}`}
                  className="brutal-btn w-full text-xs border border-[#00E676] text-[#00E676] hover:bg-[#00E676] hover:text-black py-1.5 flex items-center justify-center gap-1 transition-colors duration-150"
                >
                  <CheckCircle size={12} weight="bold" /> Log Service
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Service modal */}
      {serviceOn && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setServiceOn(null)}>
          <div onClick={(e) => e.stopPropagation()} className="brutal-card p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <div>
                <div className="font-mono text-xs text-[#FF4F00] tracking-widest">// LOG SERVICE</div>
                <h2 className="font-display font-black text-2xl">{serviceOn.name}</h2>
              </div>
            </div>
            <form onSubmit={logService} className="space-y-3">
              <textarea
                data-testid="tool-service-notes"
                rows={4}
                placeholder="Service notes"
                value={serviceNotes}
                onChange={(e) => setServiceNotes(e.target.value)}
                className="w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]"
              />
              <button
                type="submit"
                data-testid="tool-service-submit"
                className="brutal-btn w-full bg-[#00E676] text-black px-4 py-3 hover:opacity-90 transition-opacity duration-150"
              >
                Confirm Service Complete →
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="brutal-card p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA]">// TRANSACTION HISTORY</div>
            <h2 className="font-display font-bold text-xl">Timeline</h2>
          </div>
          <div className="font-mono text-xs text-[#52525B]">{history.length} events</div>
        </div>
        {history.length === 0 ? (
          <div className="text-center py-8 text-[#52525B] font-mono">// no transactions yet //</div>
        ) : (
          <div className="relative pl-8">
            <div className="absolute left-3 top-0 bottom-0 w-px bg-[#27272A]" />
            {history.map((h) => (
              <div key={h.id} className="relative mb-6 last:mb-0" data-testid={`timeline-item-${h.id}`}>
                <div
                  className={`absolute -left-[22px] w-4 h-4 border-2 ${
                    h.status === "returned" ? "bg-[#00E676] border-[#00E676]" : "bg-[#FF4F00] border-[#FF4F00]"
                  }`}
                />
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold flex items-center gap-2">
                      {h.status === "returned" ? (
                        <><CheckCircle size={14} weight="bold" className="text-[#00E676]" /> Returned by {h.user_name}</>
                      ) : (
                        <><ArrowsClockwise size={14} weight="bold" className="text-[#FF4F00]" /> Issued to {h.user_name}</>
                      )}
                    </div>
                    <div className="font-mono text-xs text-[#52525B] mt-1">
                      qty {h.quantity} · issued {new Date(h.issue_date).toLocaleString()}
                    </div>
                    {h.return_date && (
                      <div className="font-mono text-xs text-[#A1A1AA] mt-0.5">
                        returned {new Date(h.return_date).toLocaleString()}
                        {h.condition_on_return && ` · condition: ${h.condition_on_return}`}
                      </div>
                    )}
                    {h.due_date && (
                      <div className="font-mono text-xs text-[#52525B] mt-0.5 flex items-center gap-1">
                        <Timer size={10} /> due {new Date(h.due_date).toLocaleDateString()}
                      </div>
                    )}
                    {h.notes && <div className="text-xs text-[#A1A1AA] mt-1 italic">"{h.notes}"</div>}
                  </div>
                  <div className="flex items-center gap-1">
                    <User size={10} className="text-[#52525B]" />
                    <span className="font-mono text-[10px] text-[#52525B]">by {h.issued_by_name}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
